import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Box, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, Avatar, Menu, MenuItem, Divider, Chip, useTheme } from '@mui/material';
import { Menu as MenuIcon, Dashboard, SmartToy, Map, People, Logout, AdminPanelSettings, Wifi, WifiOff, Explore, Settings, Storage, Security, VerifiedUser, Psychology, InsertChart, FactCheck } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../hooks/useAppHooks';
import { logout } from '../../features/auth/authSlice';
import { useInactivityTimer } from '../../hooks/useInactivityTimer';

const drawerWidth = 260;

const navItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'My Robots', icon: <SmartToy />, path: '/robots' },
  { text: 'Maps', icon: <Map />, path: '/maps' },
  { text: 'SLAM Mapping', icon: <Explore />, path: '/slam' },
  { text: 'User Management', icon: <People />, path: '/admin/users', adminOnly: true },
  { text: 'Robot Registry', icon: <AdminPanelSettings />, path: '/admin/robots', adminOnly: true },
  { text: 'Fleet Analytics', icon: <InsertChart />, path: '/admin/analytics', adminOnly: true },
  { text: 'Audit Trail', icon: <FactCheck />, path: '/admin/audit', adminOnly: true },
  { text: 'Database Explorer', icon: <Storage />, path: '/admin/database', superAdminOnly: true },
  { text: 'System Settings', icon: <Settings />, path: '/admin/settings', superAdminOnly: true },
  { text: 'Licenses', icon: <VerifiedUser />, path: '/admin/licenses', superAdminOnly: true },
  { text: 'Voice AI', icon: <Psychology />, path: '/admin/voice-ai', superAdminOnly: true },
  { text: 'Security', icon: <Security />, path: '/security' },
  { text: 'Change Password', icon: <Security />, path: '/security/password' },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { isConnected, robotsOnline } = useAppSelector((state) => state.websocket);
  const muiTheme = useTheme();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isDark = muiTheme.palette.mode === 'dark';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : muiTheme.palette.divider;
  const activeBg = isDark ? 'rgba(0, 212, 170, 0.1)' : `${muiTheme.palette.primary.main}14`;

  // Auto-logout after 60 min of no input. Matches backend INACTIVITY_TIMEOUT_MINUTES.
  useInactivityTimer();

  const handleLogout = () => { setAnchorEl(null); dispatch(logout()); navigate('/login'); };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${borderColor}` }}>
        <Box
          component="img"
          src="/logo-wordmark.png"
          alt="Peribott"
          sx={{
            // v6.3.33: asset is now the Peribott wordmark (496×113, ~4.39:1,
            // transparent). height:36 => ~158px wide, so maxWidth 190 leaves
            // headroom instead of letterboxing it. width:auto is required —
            // MUI's default `width: 100%` on Box would stretch it.
            height: 36,
            width: 'auto',
            maxWidth: 190,
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>
      <List sx={{ flex: 1, px: 2, py: 2 }}>
        {navItems.filter((item) => {
          if ((item as any).superAdminOnly) return isSuperAdmin;
          if (item.adminOnly) return isAdmin;
          return true;
        }).map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{ borderRadius: 2, py: 1.5, backgroundColor: isActive ? activeBg : 'transparent', borderLeft: isActive ? `3px solid ${muiTheme.palette.primary.main}` : '3px solid transparent' }}>
                <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'text.secondary', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 600 : 400, color: isActive ? 'primary.main' : 'text.primary' }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ p: 2, borderTop: `1px solid ${borderColor}` }}>
        <Box sx={{ p: 2, borderRadius: 2, backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isConnected ? <Wifi sx={{ color: 'success.main', fontSize: 20 }} /> : <WifiOff sx={{ color: 'error.main', fontSize: 20 }} />}
          <Box>
            <Typography variant="body2" fontWeight={500}>{isConnected ? 'Connected' : 'Disconnected'}</Typography>
            <Typography variant="caption" color="text.secondary">{robotsOnline.length} robot{robotsOnline.length !== 1 ? 's' : ''} online</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` }, boxShadow: 'none', borderBottom: `1px solid ${borderColor}` }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}><MenuIcon /></IconButton>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 600 }}>{user?.email?.charAt(0).toUpperCase()}</Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <Box sx={{ px: 2, py: 1.5 }}><Typography variant="body2" fontWeight={600}>{user?.email}</Typography><Chip label={user?.role?.replace('_', ' ')} size="small" sx={{ mt: 0.5, textTransform: 'capitalize' }} /></Box>
            <Divider />
            <MenuItem onClick={handleLogout}><ListItemIcon><Logout fontSize="small" /></ListItemIcon><ListItemText>Sign Out</ListItemText></MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>{drawer}</Drawer>
        <Drawer variant="permanent" sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth } }} open>{drawer}</Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, minWidth: 0, overflowX: 'hidden', minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
